import { memo, useEffect } from 'react';
import { Pressable, View, ViewStyle } from 'react-native';
import Box from '../Box/Box';
import Image from '../Image/Image';
import Label from './components/Label';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import { LobbyWidgetProps } from './LobbyWidget.types';
import { useBreakpointProp } from '../../index';
import LobbyBadge from './components/LobbyBadge';

function LobbyWidget({
  image,
  label,
  onClick,
  hasUnread = false,
  showUnreadBadge = false,
  badgeChildren,
  badgeImageName,
  badgeBoxProps,
  isLocked = false,
  children,
}: LobbyWidgetProps) {
  const Component = hasUnread ? Animated.View : View;
  const rotation = useSharedValue(0);

  const animatedStylesProps = useBreakpointProp<ViewStyle>({
    mobile: { width: '100%', maxWidth: 53, height: '100%', maxHeight: 48 },
    tablet: { width: '100%', maxWidth: 80, height: '100%', maxHeight: 72 },
    desktop: { width: '100%', maxWidth: 80, height: '100%', maxHeight: 72 },
  });

  useEffect(() => {
    rotation.value = withRepeat(
      withTiming(-20, {
        duration: 500,
        easing: Easing.inOut(Easing.ease),
      }),
      -1,
      true,
    );
  }, [rotation]);

  const animatedStyles = useAnimatedStyle(() => {
    return {
      transform: [
        {
          rotate: `${rotation.value}deg`,
        },
      ],
    };
  });

  return (
    <Pressable onPress={onClick} testID={`lobby-widget-${label}`}>
      <Box
        variant="default"
        borderColor="borderGoldSolid"
        width={{ mobile: 100, tablet: 122, desktop: 122 }}
        height={{ mobile: 100, tablet: 122, desktop: 122 }}
        justifyContent="center"
        alignItems="center"
        backgroundColor="iconPrimary"
        position="relative"
        borderRadius="radius-full"
        borderStyle="solid"
        borderWidth="border-width-025"
        boxShadow="0px 4px 4px rgba(0, 0, 0, 0.25),0px 0px 4px #ffc700"
      >
        {(badgeImageName || badgeChildren) && !isLocked ? (
          <LobbyBadge imageName={badgeImageName} {...badgeBoxProps}>
            {badgeChildren}
          </LobbyBadge>
        ) : null}
        <Component
          style={[animatedStylesProps, hasUnread ? animatedStyles : null]}
        >
          {children ?? (
            <Image
              contentFit="contain"
              width="100%"
              height="100%"
              opacity={isLocked ? 0.5 : 1}
              name={image}
              priority="high"
              cachePolicy="memory-disk"
              transform={[{ rotate: hasUnread ? '10deg' : '0deg' }]}
            />
          )}
        </Component>
        {isLocked && (
          <Image
            contentFit="contain"
            width={{ mobile: 105, tablet: 126, desktop: 126 }}
            height="100%"
            position="absolute"
            name="common.chainsLocked"
            priority="high"
            cachePolicy="memory-disk"
          />
        )}
        <Box
          position="absolute"
          alignItems="center"
          bottom="space-0"
          width={{ mobile: 122, tablet: 180, desktop: 180 }}
        >
          <Label label={label} hasUnread={showUnreadBadge && hasUnread} />
        </Box>
      </Box>
    </Pressable>
  );
}

export default memo(LobbyWidget);
